import * as Sentry from '@sentry/react';
import { CLIENT_TYPES } from '../constant';
import { get, post, put, URLs } from './request';

export {
  activeOrderCount,
  getOrders,
  putCancelOrder,
  postOrderReview,
  postDriverReview,
  getDriverLocation,
  getFollowupPaymentInfo,
};

async function activeOrderCount() {
  const { data } = await get({
    url: `/api/orders/active-order-count`,
    options: {
      API_BASE: 'thisServer',
    },
  });
  return data;
}

async function getOrders(page, include_pending = true) {
  const { data } = await get({
    url: `${URLs.api}/v4/orders`,
    options: { CORS_API: true },
    data: {
      page,
      include_pending,
    },
  });
  return data;
}

async function putCancelOrder(number) {
  return await put({
    url: `${URLs.api}/orders/${number}/cancel`,
    options: { CORS_API: true },
  });
}

async function postOrderReview(mealsReviews) {
  return await post({
    url: `${URLs.api}/reviews`,
    data: {
      reviews: mealsReviews.map((review) => ({
        meal_id: review.id,
        order_id: review.orderId,
        rate: review.rate,
        content: review.comment,
      })),
    },
  });
}

async function postDriverReview({ driverId, orderNumber, rate, content }) {
  return await post({
    url: `${URLs.sona}/api/diner/v1/reviews`,
    data: {
      agent_id: driverId,
      order_number: orderNumber,
      rate,
      content,
    },
  });
}

async function getDriverLocation(driverId) {
  const { data } = await get({ url: `${URLs.sona}/api/diner/v1/locations/${driverId}` });
  return data;
}

/**
 * When an order is placed, PaymentUri comes back with the order.
 * It has all the details of the payment that is being made for that order.
 * @param {string} paymentUri
 * @returns {Promise<OrderState.PaymentInfo>}
 */
async function getFollowupPaymentInfo(paymentUri) {
  // paymentUri is dynamic , use CORS_API instead of NEXT_PUBLIC_CORS_API
  const result = await get({
    url: paymentUri,
    options: { API_BASE: process.env.API_BASE, CORS_API: true },
  });
  /** @type {OrderState.PaymentInfo} */
  const paymentInfo = result.data.data;
  // Some (stripe) payments will have a client_payment_continuation_info that JSON encoded inside the larger JSON
  const rawContinuation = paymentInfo?.attributes?.client_payment_continuation_info;
  // stripe continuation is json, but others, like nihaopay are html
  const isStripe = paymentInfo?.attributes?.processor === CLIENT_TYPES.STRIPE;
  if (rawContinuation && isStripe) {
    try {
      const continuation = JSON.parse(rawContinuation);
      paymentInfo.attributes = {
        ...paymentInfo.attributes,
        ...continuation,
      };
    } catch (e) {
      Sentry.captureException(new MalformedPaymentContinuationException(e));
    }
  }
  return paymentInfo;
}

/**
 * Fired when we know a client_payment_continuation_info should be JSON parseable, but isn't.
 * @link https://chowbus.atlassian.net/browse/DW-431
 */
class MalformedPaymentContinuationException extends Error {
  constructor(sourceError) {
    super(
      `Stripe payment client_payment_continuation_info was not JSON parsable. Source Error: ${sourceError.message}`
    );
    this.name = 'MalformedPaymentContinuationException';
  }
}
